/**
 * "Ny kund" – samma dialog på Affärer – Pipeline (byggnadsikonen) och i
 * Kunder-listan.
 *
 * Snabbfyll: ett inklistrat bokningsmail fyller företagsuppgifter; all annan
 * inklistrad text tolkas som mailsignatur med AI (modules/crm/signatureAI.js)
 * och fyller kund- och kontaktfälten. Dubblettkontrollen föreslår befintlig
 * kund (samma e-postdomän eller liknande namn); där fylls bara tomma fält och
 * avvikande värden väljs av användaren. Org.nr kan slås upp hos BolagsAPI och
 * LinkedIn-sidan berikar.
 *
 * Komponenten monteras per öppning (föräldern renderar den villkorat), så att
 * formuläret alltid börjar tomt.
 */
import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, doc, updateDoc } from 'firebase/firestore';
import {
  Box, Typography, Button, TextField, IconButton, Chip, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Tooltip, Divider, Alert, RadioGroup, Radio, FormControlLabel,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { db } from '../../firebase/config';
import { authFetch } from '../../utils/authFetch';
import { deptCode } from '../../utils/depts';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { enrichCompany, mapCompanyToForm } from '../../services/enrichLayer';
import {
  parseSignature, toFormValues, findCompanyMatches, mergePlan, COMPANY_FIELD_LABELS, CONTACT_FIELD_LABELS,
} from './signatureAI';
import MergeChoices, { pickUpdates } from './MergeChoices';
import ContactFieldsSection, { EMPTY_NEW_CONTACT, contactIsSavable, contactHasData } from './ContactFieldsSection';
import { sparaKundlogga } from './bildkopia';
import {
  EMPTY_COMPANY_FORM, COMPANY_STATUSES_LIST, insertBullet, normalizeOrgNr, orgNrProblem, parseBookingEmail,
} from './kundformular';
import { SegmentValjare, OrganisationstypValjare } from './KategoriVal';

/**
 * @param {object} p
 *   open, onClose
 *   companies    alla kunder (dubblettkontroll)
 *   departments  affärsområden (val och MVL vid bokningsmail)
 *   uid          inloggad användare (createdBy)
 *   onSparad({ kund, kontaktId, ny }) – ny kund (ny=true) eller kontakt på befintlig kund
 */
export default function NyKundDialog({ open, onClose, companies, departments, uid, onSparad }) {
  const [newCompanyForm, setNewCompanyForm] = useState(EMPTY_COMPANY_FORM);
  const [newCompPaste, setNewCompPaste] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyLookingUp, setCompanyLookingUp] = useState(false);
  const [companyLookupMsg, setCompanyLookupMsg] = useState('');
  const [companyEnriching, setCompanyEnriching] = useState(false);
  const [companyEnrichMsg, setCompanyEnrichMsg] = useState('');
  // Signaturtolkning i "Ny kund". sigAi håller de värden modellen fyllde i, så
  // att fälten kan markeras så länge användaren inte ändrat dem.
  const [newContactForm, setNewContactForm] = useState(EMPTY_NEW_CONTACT);
  const [sigStatus, setSigStatus] = useState('idle'); // idle | loading | error | done
  const [sigError, setSigError] = useState('');
  const [sigMulti, setSigMulti] = useState(false);
  const [sigAi, setSigAi] = useState({ company: {}, contact: {}, inferred: [] });
  const [sigMatches, setSigMatches] = useState([]);
  const [sigTargetId, setSigTargetId] = useState(''); // '' = skapa ny kund
  const [sigChoices, setSigChoices] = useState({});
  const [targetContacts, setTargetContacts] = useState([]);
  const [contactDupChoice, setContactDupChoice] = useState('update');

  async function handleBolagsLookupNewCompany() {
    if (!newCompanyForm.orgNumber?.trim()) return;
    const problem = orgNrProblem(newCompanyForm.orgNumber);
    if (problem) { setCompanyLookupMsg(problem); return; }
    const orgNr = normalizeOrgNr(newCompanyForm.orgNumber);
    setCompanyLookingUp(true);
    setCompanyLookupMsg('');
    try {
      const res = await authFetch(`/.netlify/functions/bolagsverket?orgnr=${encodeURIComponent(orgNr)}`);
      const data = await res.json();
      if (!res.ok) { setCompanyLookupMsg(data.error || `Fel ${res.status}`); return; }
      setNewCompanyForm(f => ({
        ...f,
        name:     data.name          || f.name,
        address:  data.address?.street || f.address,
        city:     data.address?.city   || f.city,
        website:  data.website         || f.website,
        industry: f.industry || (data.sniCodes?.[0]?.label ?? ''),
      }));
      setCompanyLookupMsg(`Hämtade: ${data.name}`);
    } catch (e) {
      setCompanyLookupMsg('Nätverksfel: ' + e.message);
    } finally {
      setCompanyLookingUp(false);
    }
  }

  async function handleLinkedInEnrichNewCompany() {
    if (!newCompanyForm.linkedinUrl) return;
    setCompanyEnriching(true);
    setCompanyEnrichMsg('');
    try {
      const data = await enrichCompany(newCompanyForm.linkedinUrl);
      const mapped = mapCompanyToForm(data);
      setNewCompanyForm(f => ({
        ...f,
        name:        mapped.name        || f.name,
        website:     mapped.website     || f.website,
        description: mapped.description || f.description,
        employees:   mapped.employees   || f.employees,
        foundedYear: mapped.foundedYear || f.foundedYear,
        companyType: mapped.companyType || f.companyType,
        city:        mapped.city        || f.city,
        address:     mapped.address     || f.address,
        specialties: mapped.specialties || f.specialties,
        // Loggan måste följa med – annars syns den först efter ny hämtning på kundsidan.
        logoUrl:     mapped.logoUrl     || f.logoUrl || '',
      }));
      setCompanyEnrichMsg(`Berikade: ${mapped.name || 'klart'}`);
    } catch (e) {
      setCompanyEnrichMsg(e.message || 'Kunde inte hämta data');
    } finally {
      setCompanyEnriching(false);
    }
  }

  // Inklistrad text som inte är ett bokningsmail tolkas som mailsignatur.
  // Resultatet fyller bara tomma fält – inget sparas förrän användaren klickar.
  async function runSignatureParse(text) {
    setSigStatus('loading');
    setSigError('');
    try {
      const result = await parseSignature(text);
      const { company, contact, inferredKeys } = toFormValues(result);
      if (company.orgNumber) company.orgNumber = normalizeOrgNr(company.orgNumber) || company.orgNumber;
      const fillEmpty = (f, vals) => ({
        ...f,
        ...Object.fromEntries(Object.entries(vals).filter(([k]) => !String(f[k] || '').trim())),
      });
      setNewCompanyForm(f => fillEmpty(f, company));
      setNewContactForm(f => fillEmpty(f, contact));
      setSigAi({ company, contact, inferred: inferredKeys });
      setSigMulti(result.multipleSignatures);
      const matches = findCompanyMatches(companies, {
        name: company.name, website: company.website, email: contact.email,
      });
      setSigMatches(matches);
      setSigTargetId(matches[0]?.company.id || '');
      setSigChoices({});
      setContactDupChoice('update');
      setSigStatus('done');
    } catch (e) {
      console.error('Signaturtolkning misslyckades:', e);
      setSigStatus('error');
      setSigError('Det gick inte att tolka texten automatiskt. Fyll i uppgifterna manuellt – texten finns kvar i rutan.');
    }
  }

  // Dubblettkontroll på kontakt: e-post mot kontakterna på vald befintlig kund.
  useEffect(() => {
    if (!sigTargetId) { setTargetContacts([]); return; }
    let cancelled = false;
    getDocs(collection(db, 'crmCompanies', sigTargetId, 'contacts'))
      .then(snap => { if (!cancelled) setTargetContacts(snap.docs.map(d => ({ id: d.id, ...d.data() }))); })
      .catch(e => console.error('Kunde inte läsa kontakter:', e));
    return () => { cancelled = true; };
  }, [sigTargetId]);

  const sigTarget = sigTargetId ? companies.find(c => c.id === sigTargetId) : null;
  const newContactEmail = newContactForm.email.trim().toLowerCase();
  const dupContact = sigTarget && newContactEmail
    ? targetContacts.find(c => (c.email || '').trim().toLowerCase() === newContactEmail) || null
    : null;
  const companyMergeRows = sigTarget ? mergePlan(sigTarget, sigAi.company, COMPANY_FIELD_LABELS) : [];
  const contactMergeRows = dupContact ? mergePlan(dupContact, newContactForm, CONTACT_FIELD_LABELS) : [];

  // Diskret markering av fält som tolkningen fyllt i, så länge värdet är orört.
  function sigMark(scope, key, value) {
    const aiVal = sigAi[scope]?.[key];
    if (!aiVal || value !== aiVal) return {};
    const inferred = scope === 'company' && sigAi.inferred.includes(key);
    return {
      helperText: inferred ? 'Härlett från e-postdomän' : 'AI-ifyllt',
      FormHelperTextProps: { sx: { color: inferred ? '#8D6E63' : '#7E57C2', mt: 0.25, fontSize: '0.68rem' } },
    };
  }

  // Returnerar id för kontakten som skapades eller uppdaterades, annars ''.
  async function saveNewContact(companyId, { primary }) {
    if (!contactHasData(newContactForm)) return '';
    const data = Object.fromEntries(
      Object.entries(newContactForm).map(([k, v]) => [k, String(v || '').trim()])
    );
    if (dupContact && contactDupChoice === 'update') {
      const updates = pickUpdates(contactMergeRows, sigChoices, 'contact.');
      if (Object.keys(updates).length) {
        await updateDoc(doc(db, 'crmCompanies', companyId, 'contacts', dupContact.id), updates);
      }
      return dupContact.id;
    }
    const ref = await addDoc(collection(db, 'crmCompanies', companyId, 'contacts'), {
      ...data, email: data.email.toLowerCase(), primary, dealRelation: '', notes: '',
    });
    return ref.id;
  }

  async function handleCreateCompany() {
    if (!newCompanyForm.name.trim() || !contactIsSavable(newContactForm)) return;
    setSavingCompany(true);
    try {
      const docRef = await addDoc(collection(db, 'crmCompanies'), {
        ...newCompanyForm,
        name: newCompanyForm.name.trim(),
        createdBy: uid || '',
        createdAt: new Date(),
      });
      sparaKundlogga(docRef.id, newCompanyForm); // i bakgrunden
      const contactId = await saveNewContact(docRef.id, { primary: true });
      onSparad?.({ kund: { id: docRef.id, ...newCompanyForm, name: newCompanyForm.name.trim() }, kontaktId: contactId, ny: true });
      onClose();
    } finally {
      setSavingCompany(false);
    }
  }

  // Signaturen hörde till en kund som redan finns: fyll tomma fält, skriv
  // avvikande värden bara där användaren valt det, och lägg till kontakten.
  async function handleSaveToExistingCompany() {
    if (!sigTarget || !contactIsSavable(newContactForm)) return;
    setSavingCompany(true);
    try {
      const updates = pickUpdates(companyMergeRows, sigChoices, 'company.');
      if (Object.keys(updates).length) {
        await updateDoc(doc(db, 'crmCompanies', sigTarget.id), { ...updates, updatedAt: new Date() });
      }
      const contactId = await saveNewContact(sigTarget.id, { primary: false });
      onSparad?.({ kund: { ...sigTarget, ...updates }, kontaktId: contactId, ny: false });
      onClose();
    } finally {
      setSavingCompany(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BusinessIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">Ny kund</Typography>
            <Typography variant="caption" color="text.secondary">Fyll i det du vet – du kan komplettera senare</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />

      {/* ── Processvägledning ── */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
          {[
            { n: 1, label: 'Org.nr',          done: !!newCompanyForm.orgNumber },
            { n: 2, label: 'Hämta BolagsAPI', done: companyLookupMsg.startsWith('Hämtade') },
            { n: 3, label: 'LinkedIn',         done: !!newCompanyForm.linkedinUrl },
            { n: 4, label: 'Hämta LinkedIn',   done: companyEnrichMsg.startsWith('Berikade') },
            { n: 5, label: 'Granska & fyll i', done: !!newCompanyForm.name },
            { n: 6, label: 'Spara',            done: false },
          ].map((step, i, arr) => {
            const active = !step.done && (i === 0 || arr[i-1].done);
            return (
              <React.Fragment key={step.n}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: step.done ? '#2E7D32' : active ? '#FF6B00' : '#e0e0e0',
                    color: step.done || active ? 'white' : '#999',
                  }}>
                    {step.done ? '✓' : step.n}
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: '0.68rem', fontWeight: active ? 700 : 400,
                    color: step.done ? '#2E7D32' : active ? '#FF6B00' : 'text.disabled',
                    whiteSpace: 'nowrap',
                  }}>
                    {step.label}
                  </Typography>
                </Box>
                {i < arr.length - 1 && (
                  <Typography sx={{ color: '#ccc', fontSize: '0.7rem', mx: 0.5 }}>›</Typography>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ── Snabbfyll från bokningsmail ── */}
          <Box sx={{ bgcolor: '#fff8e1', borderRadius: 2, p: 2, border: '1px solid #ffe082' }}>
            <Typography variant="caption" fontWeight="bold"
              sx={{ color: '#E65100', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Snabbfyll — klistra in bokningsmail eller mailsignatur
            </Typography>
            <TextField fullWidth size="small" multiline minRows={1} maxRows={10}
              placeholder="Klistra in hela bokningsmailet från säljsystemet, eller en mailsignatur"
              value={newCompPaste}
              disabled={sigStatus === 'loading'}
              onChange={e => {
                const val = e.target.value;
                setNewCompPaste(val);
                const b = parseBookingEmail(val);
                // Bokningsmailet har företräde. Allt annat som klistras in
                // tolkas som signatur; inskriven text tolkas först på knapptryck.
                if (!b && e.nativeEvent?.inputType === 'insertFromPaste' && val.trim().length > 10) {
                  runSignatureParse(val);
                }
                if (b) {
                  // Bokningsmailen kommer från Comdeva och landar alltid på MVL.
                  const mvl = departments.find(d => d.name?.toUpperCase().includes('MVL'));
                  setNewCompanyForm(f => ({
                    ...f,
                    ...(b.companyName && !f.name ? { name: b.companyName } : {}),
                    ...(b.orgNumber && !f.orgNumber ? { orgNumber: b.orgNumber } : {}),
                    ...(b.city && !f.city ? { city: b.city } : {}),
                    ...(b.employees && !f.employees ? { employees: b.employees } : {}),
                    ...(b.industry && !f.industry ? { industry: b.industry } : {}),
                    ...(b.revenue && !f.revenue ? { revenue: b.revenue } : {}),
                    ...(mvl && !(f.departmentIds || []).includes(mvl.id)
                      ? { departmentIds: [...(f.departmentIds || []), mvl.id] } : {}),
                  }));
                }
              }}
              helperText={parseBookingEmail(newCompPaste)
                ? 'Bokningsmail: företagsnamn, org.nr, ort, bransch och omsättning fylls i — och MVL kryssas i'
                : 'Bokningsmail fyller i företagsuppgifter. En signatur tolkas med AI och fyller kund och kontakt.'}
              InputProps={{ sx: { bgcolor: 'white' } }}
            />
            {newCompPaste.trim() && !parseBookingEmail(newCompPaste) && sigStatus !== 'loading' && (
              <Button size="small" startIcon={<AutoFixHighIcon />} onClick={() => runSignatureParse(newCompPaste)}
                sx={{ mt: 1, color: '#7E57C2' }}>
                {sigStatus === 'done' ? 'Tolka signaturen igen' : 'Tolka som mailsignatur'}
              </Button>
            )}
            {sigStatus === 'loading' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Tolkar signaturen…</Typography>
              </Box>
            )}
            {sigStatus === 'error' && (
              <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setSigStatus('idle')}>
                <Typography variant="caption">{sigError}</Typography>
              </Alert>
            )}
            {sigStatus === 'done' && sigMulti && (
              <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                <Typography variant="caption">Texten innehöll flera signaturer — uppgifterna kommer från den översta.</Typography>
              </Alert>
            )}
          </Box>

          {/* ── Dubblettkontroll efter signaturtolkning ── */}
          {sigStatus === 'done' && sigMatches.length > 0 && (
            <Box sx={{ borderRadius: 2, p: 2, border: '1px solid #b39ddb', bgcolor: '#f7f3ff' }}>
              <Typography variant="caption" fontWeight="bold" color="#5E35B1"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
                Kunden kan redan finnas
              </Typography>
              <RadioGroup value={sigTargetId} onChange={e => { setSigTargetId(e.target.value); setSigChoices({}); }}>
                {sigMatches.map(m => (
                  <FormControlLabel key={m.company.id} value={m.company.id} control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        Lägg till kontakt på befintlig kund <b>{m.company.name}</b>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {m.reason === 'domain' ? 'samma e-postdomän' : 'liknande namn'}
                        </Typography>
                      </Typography>
                    } />
                ))}
                <FormControlLabel value="" control={<Radio size="small" />}
                  label={<Typography variant="body2">Skapa ny kund ändå</Typography>} />
              </RadioGroup>
              {sigTarget && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #d1c4e9' }}>
                  <MergeChoices rows={companyMergeRows} choices={sigChoices} prefix="company."
                    onChoose={(k, v) => setSigChoices(c => ({ ...c, [k]: v }))} />
                </Box>
              )}
            </Box>
          )}

          {sigTarget && (
            <ContactFieldsSection
              value={newContactForm} onChange={setNewContactForm}
              mark={(k, v) => sigMark('contact', k, v)}
              dupContact={dupContact} dupChoice={contactDupChoice} onDupChoice={setContactDupChoice}
              plan={contactMergeRows} choices={sigChoices}
              onChoose={(k, v) => setSigChoices(c => ({ ...c, [k]: v }))} />
          )}

          {!sigTarget && (<>

          {/* ── 1. Identitet ── */}
          <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
              Identitet
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField size="small" label="Org.nr" placeholder="556000-0000" autoFocus
                sx={{ width: 200, flexShrink: 0 }}
                value={newCompanyForm.orgNumber}
                onChange={e => { setNewCompanyForm(f => ({ ...f, orgNumber: e.target.value })); setCompanyLookupMsg(''); }}
                {...sigMark('company', 'orgNumber', newCompanyForm.orgNumber)}
                InputProps={{
                  endAdornment: newCompanyForm.orgNumber && (
                    <Tooltip title="Hämta företagsinfo via BolagsAPI">
                      <IconButton size="small" onClick={handleBolagsLookupNewCompany} disabled={companyLookingUp}
                        sx={{ borderRadius: 1, p: '3px' }}>
                        {companyLookingUp
                          ? <CircularProgress size={14} />
                          : <Box component="img" src="/bolagsapi_logo.svg" alt="BolagsAPI"
                              sx={{ height: 16, width: 'auto' }} />}
                      </IconButton>
                    </Tooltip>
                  )
                }} />
              <TextField size="small" fullWidth label="LinkedIn-sida"
                placeholder="https://www.linkedin.com/company/..."
                value={newCompanyForm.linkedinUrl}
                onChange={e => { setNewCompanyForm(f => ({ ...f, linkedinUrl: e.target.value })); setCompanyEnrichMsg(''); }}
                InputProps={{
                  endAdornment: newCompanyForm.linkedinUrl && (
                    <Tooltip title="Hämta företagsinfo från LinkedIn">
                      <IconButton size="small" onClick={handleLinkedInEnrichNewCompany} disabled={companyEnriching}
                        sx={{ borderRadius: 1, p: '3px', color: '#0077b5', '&:hover': { bgcolor: 'rgba(0,119,181,0.1)' } }}>
                        {companyEnriching ? <CircularProgress size={14} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  )
                }} />
            </Box>
            {(companyLookupMsg || companyEnrichMsg) && (
              <Alert severity={(companyLookupMsg || companyEnrichMsg).startsWith('Hämtade') || (companyLookupMsg || companyEnrichMsg).startsWith('Berikade') ? 'success' : 'error'}
                sx={{ mt: 1, py: 0 }} onClose={() => { setCompanyLookupMsg(''); setCompanyEnrichMsg(''); }}>
                <Typography variant="caption">{companyLookupMsg || companyEnrichMsg}</Typography>
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
              <TextField size="small" fullWidth label="Företagsnamn *"
                placeholder="Fylls i av BolagsAPI / LinkedIn"
                value={newCompanyForm.name}
                onChange={e => setNewCompanyForm(f => ({ ...f, name: e.target.value }))}
                {...sigMark('company', 'name', newCompanyForm.name)} />
              <TextField select size="small" label="Kundstatus" sx={{ width: 180, flexShrink: 0 }}
                value={newCompanyForm.status}
                onChange={e => setNewCompanyForm(f => ({ ...f, status: e.target.value }))}>
                {COMPANY_STATUSES_LIST.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
              </TextField>
            </Box>
            {departments.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
                  Kopplade affärsområden
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {departments.map(d => {
                    const sel = (newCompanyForm.departmentIds || []).includes(d.id);
                    return (
                      <Tooltip key={d.id} title={d.name}>
                        <Chip label={deptCode(d)} size="small"
                          onClick={() => setNewCompanyForm(f => ({
                            ...f,
                            departmentIds: sel
                              ? f.departmentIds.filter(id => id !== d.id)
                              : [...(f.departmentIds || []), d.id]
                          }))}
                          variant={sel ? 'filled' : 'outlined'}
                          sx={{ bgcolor: sel ? '#1a1a1a' : 'transparent',
                            color: sel ? 'white' : 'text.primary', cursor: 'pointer' }} />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* ── 2. Affärsinformation ── */}
          <Box sx={{ bgcolor: '#f0f4ff', borderRadius: 2, p: 2 }}>
            <Typography variant="caption" fontWeight="bold" color="#1565C0"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
              Affärsinformation
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {/* Bransch, ägarform och kundtyp döljs (datan finns kvar) – segment och organisationstyp ersätter dem */}
              <SegmentValjare sx={{ flex: '1 1 220px' }}
                value={newCompanyForm.segment || ''}
                onChange={v => setNewCompanyForm(f => ({ ...f, segment: v }))} />
              <OrganisationstypValjare sx={{ flex: '1 1 200px' }}
                value={newCompanyForm.organisationstyp || ''}
                onChange={v => setNewCompanyForm(f => ({ ...f, organisationstyp: v }))} />
              <TextField size="small" label="Antal anställda" placeholder="t.ex. 250" sx={{ flex: '0 1 140px' }}
                value={newCompanyForm.employees}
                onChange={e => setNewCompanyForm(f => ({ ...f, employees: e.target.value }))} />
              <TextField size="small" label="Omsättning" placeholder="t.ex. 120 MSEK" sx={{ flex: '0 1 160px' }}
                value={newCompanyForm.revenue}
                onChange={e => setNewCompanyForm(f => ({ ...f, revenue: e.target.value }))} />
              <TextField size="small" label="Telefon (växel)" placeholder="+46 8 123 456" sx={{ flex: '0 1 160px' }}
                value={newCompanyForm.phone}
                onChange={e => setNewCompanyForm(f => ({ ...f, phone: e.target.value }))}
                {...sigMark('company', 'phone', newCompanyForm.phone)} />
              <TextField size="small" label="Webbplats" placeholder="www.foretaget.se" sx={{ flex: '1 1 200px' }}
                value={newCompanyForm.website}
                onChange={e => setNewCompanyForm(f => ({ ...f, website: e.target.value }))}
                {...sigMark('company', 'website', newCompanyForm.website)} />
            </Box>
          </Box>

          {/* ── 3. Postadress ── */}
          <Box sx={{ bgcolor: '#f0faf4', borderRadius: 2, p: 2 }}>
            <Typography variant="caption" fontWeight="bold" color="#2E7D32"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
              Postadress
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <AddressAutocomplete
                size="small"
                value={newCompanyForm.address}
                onChange={({ address, city, postcode }) => setNewCompanyForm(f => ({
                  ...f, address, city: city || f.city, zip: postcode || f.zip,
                }))}
              />
              <TextField size="small" label="Postnr" value={newCompanyForm.zip || ''}
                onChange={e => setNewCompanyForm(f => ({ ...f, zip: e.target.value }))}
                {...sigMark('company', 'zip', newCompanyForm.zip)}
                sx={{ flex: '0 1 100px' }} />
              <TextField size="small" label="Ort" value={newCompanyForm.city || ''}
                onChange={e => setNewCompanyForm(f => ({ ...f, city: e.target.value }))}
                {...sigMark('company', 'city', newCompanyForm.city)}
                sx={{ flex: '0 1 160px' }} />
              <TextField size="small" label="Land" placeholder="Sverige" value={newCompanyForm.country || ''}
                onChange={e => setNewCompanyForm(f => ({ ...f, country: e.target.value }))}
                {...sigMark('company', 'country', newCompanyForm.country)}
                sx={{ flex: '0 1 140px' }} />
            </Box>
            {sigMark('company', 'address', newCompanyForm.address).helperText && (
              <Typography variant="caption" sx={{ color: '#7E57C2', fontSize: '0.68rem' }}>Gatuadress AI-ifylld</Typography>
            )}
            <Typography variant="caption" fontWeight="bold" color="#2E7D32"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mt: 2, mb: 1.5 }}>
              Besöksadress
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField size="small" label="Gatuadress" value={newCompanyForm.visitStreet || ''}
                onChange={e => setNewCompanyForm(f => ({ ...f, visitStreet: e.target.value }))}
                {...sigMark('company', 'visitStreet', newCompanyForm.visitStreet)}
                sx={{ flex: '1 1 220px' }} />
              <TextField size="small" label="Postnr" value={newCompanyForm.visitZip || ''}
                onChange={e => setNewCompanyForm(f => ({ ...f, visitZip: e.target.value }))}
                {...sigMark('company', 'visitZip', newCompanyForm.visitZip)}
                sx={{ flex: '0 1 100px' }} />
              <TextField size="small" label="Ort" value={newCompanyForm.visitCity || ''}
                onChange={e => setNewCompanyForm(f => ({ ...f, visitCity: e.target.value }))}
                {...sigMark('company', 'visitCity', newCompanyForm.visitCity)}
                sx={{ flex: '0 1 160px' }} />
            </Box>
          </Box>

          {/* ── 4. LinkedIn-berikade fält ── */}
          {(newCompanyForm.description || newCompanyForm.foundedYear || newCompanyForm.companyType || newCompanyForm.specialties) && (
            <Box sx={{ bgcolor: 'rgba(0,119,181,0.05)', borderRadius: 2, p: 2, border: '1px solid rgba(0,119,181,0.15)' }}>
              <Typography variant="caption" sx={{ color: '#0077b5', fontWeight: 700, display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                LinkedIn-data
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {newCompanyForm.description && (
                  <TextField size="small" fullWidth label="Beskrivning" multiline rows={2}
                    value={newCompanyForm.description}
                    onChange={e => setNewCompanyForm(f => ({ ...f, description: e.target.value }))} />
                )}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {newCompanyForm.foundedYear && (
                    <TextField size="small" label="Grundat år" sx={{ width: 140 }}
                      value={newCompanyForm.foundedYear}
                      onChange={e => setNewCompanyForm(f => ({ ...f, foundedYear: e.target.value }))} />
                  )}
                  {newCompanyForm.companyType && (
                    <TextField size="small" label="Bolagstyp"
                      value={newCompanyForm.companyType}
                      onChange={e => setNewCompanyForm(f => ({ ...f, companyType: e.target.value }))} />
                  )}
                </Box>
                {newCompanyForm.specialties && (
                  <TextField size="small" fullWidth label="Specialiteter"
                    value={newCompanyForm.specialties}
                    onChange={e => setNewCompanyForm(f => ({ ...f, specialties: e.target.value }))} />
                )}
              </Box>
            </Box>
          )}

          {/* ── 5. Notering ── */}
          {(() => {
            const ref = React.createRef();
            return (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.25 }}>
                  <Tooltip title="Infoga punkt (•)">
                    <IconButton size="small" sx={{ fontSize: '0.75rem', px: 0.75, py: 0.25, borderRadius: 1,
                      color: 'text.secondary', '&:hover': { color: '#FF6B00' } }}
                      onClick={() => insertBullet(ref, newCompanyForm.notes, v => setNewCompanyForm(f => ({ ...f, notes: v })))}>
                      • Lista
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField ref={ref} fullWidth size="small" label="Notering / bakgrund" multiline rows={3}
                  placeholder="Vad vet du om kunden, hur togs kontakten..."
                  value={newCompanyForm.notes}
                  onChange={e => setNewCompanyForm(f => ({ ...f, notes: e.target.value }))} />
              </Box>
            );
          })()}

          <ContactFieldsSection
            value={newContactForm} onChange={setNewContactForm}
            mark={(k, v) => sigMark('contact', k, v)}
            dupContact={null} plan={[]} choices={sigChoices} onChoose={() => {}} />
          </>)}

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Button onClick={onClose} size="large">Avbryt</Button>
        {sigTarget ? (
          <Button variant="contained" onClick={handleSaveToExistingCompany} size="large"
            disabled={savingCompany || companyEnriching || !contactIsSavable(newContactForm)}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {savingCompany ? 'Sparar...' : `Spara på ${sigTarget.name}`}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleCreateCompany} size="large"
            disabled={savingCompany || companyEnriching || !newCompanyForm.name.trim() || !contactIsSavable(newContactForm)}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {savingCompany ? 'Sparar...' : 'Spara företag'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
